// @givmo/board — reads the signing window gates (CBG-SEC2 SEC2-WEB; contract §4.6).
//
// `board_director_signing_required` refuses reads as well as acts: a named executive-session record.
// The shared sheet answers it through the same gate as the acts (Account Security, 2026-10-02 22:43
// PDT, answer 6: "Run the same gate over the refetch; once by itself, then an inline line with one
// button if it's dismissed").
//
// THERE IS NO BOARD-LEVEL READ GATE. A board could once adopt "confirm before anything opens", and
// its reads were held behind an ask until the window opened. That option is retired (D-CBG-20,
// CBG-SEC2-ACC1: every board has one rule), and the backend reads a stored adoption as the default,
// so a director reads and replies on their account's sign-in. `/me`'s retired `access_mode` and
// `step_up_required_for_all_access` are not read at all.
//
// A REFUSED READ — an executive-session record, or a window that shut between `/me` and the read —
// opens the sheet by itself once for that refusal, over a refetch of the refused reads; once a
// confirmation has cleared it, a later lapse that refuses again is a new refusal and asks again.
// Either way a dismissal leaves the backend's own sentence and one button, and a refusal standing
// with no sheet up always shows them. That button is a way back into content the director was
// refused; it is not the closed-window "Confirm to sign" Jonathan removed, which offered a
// confirmation with nothing to open.
//
// DURING THE PAUSE AFTER A SIGN-IN METHOD IS REMOVED (CBG-FIX127; `signing-pause.ts`) no confirmation
// can open it, so a refused read brings up no sheet and offers no button: its card says when it opens
// (S2). Once the pause ends the card is the dismissed one above, its button the way back in. A
// removal Board can't read serves no end (S3), and the card is today's.

import { useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, testIdProp } from '@givmo/design-system';
import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { useSearch } from '../board-routing';
import { directorOrgOf } from './director-board';
import { boardDirectorMeKey, directorBoardFor, useDirectorMe } from './director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from './director-wire';
import { openExecutiveRowFor } from './signing-copy';
import { useBoardSigningGate } from './signing-gate';
import { signingPauseWords, useSigningPause } from './signing-pause';

/** Whether a caught failure is the window's refusal. */
export function isSigningRefusal(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === DIRECTOR_REFUSALS.signingRequired;
}

/** The backend's own sentence for a refusal, or the gate's fallback when it carries none. */
function refusalSentence(error: unknown, fallback: string): string {
  return isGivmoError(error) && error.message.trim().length > 0 ? error.message : fallback;
}

/**
 * Whether this board's reads may go out: once `/me` names it as one of this account's boards,
 * whatever the window says (there is no board-level read gate, D-CBG-20). False while `/me` is
 * pending and for a board that isn't this account's; true when `/me` failed (fail open: the backend
 * still decides).
 */
export function useDirectorReadsMayFire(orgRef: string | null): boolean {
  const meQuery = useDirectorMe();
  if (orgRef === null) return false;
  if (meQuery.isError) return true;
  if (!meQuery.isSuccess) return false;
  return directorBoardFor(meQuery.data, orgRef) !== null;
}

/**
 * The fallback for a refused read that carries no sentence of its own: the backend's own words,
 * approved in copy round 2 (D-CBG-18), in the executive-session sheet's terms; and, for a seat for
 * someone who isn't on the board, the backend's committee sentence (CBG-FIX106, row 4.11).
 */
const EXECUTIVE_ASKS =
  'This board asks you to confirm it’s you before executive-session material opens.';
const COMMITTEE_EXECUTIVE_ASKS =
  'This committee asks you to confirm it’s you before executive-session material opens.';
export const DID_NOT_OPEN = 'It didn’t open. Try again, or sign out and back in.';

interface RefusableRead {
  isError: boolean;
  error: unknown;
  refetch: (options?: { throwOnError?: boolean }) => Promise<unknown>;
}

/**
 * A refused read: the sheet once by itself over a refetch of the refused reads, then the backend's
 * own sentence and one button. Renders nothing while no read is refused.
 */
export function DirectorReadRefusal({ reads, testId }: { reads: RefusableRead[]; testId: string }) {
  const queryClient = useQueryClient();
  const meQuery = useDirectorMe();
  const accountHasPasskey = meQuery.data?.signing.account_has_passkey;
  // A read is refused for executive-session material, so the sheet is worded for that — a
  // committee's, to a seat for someone who isn't on the board (CBG-FIX106).
  const committeeOnly = isCommitteeOnly(directorBoardFor(meQuery.data, directorOrgOf(useSearch())));
  const gate = useBoardSigningGate(openExecutiveRowFor(committeeOnly));
  const refused = reads.filter((read) => read.isError && isSigningRefusal(read.error));
  const first = refused[0];
  const pauseEnd = useSigningPause();
  const [state, setState] = useState<'idle' | 'asking' | 'dismissed' | 'refused'>('idle');
  const asked = useRef(false);

  const ask = async () => {
    setState('asking');
    const outcome = await gate.run(async () => {
      for (const read of refused) await read.refetch({ throwOnError: true });
    }, accountHasPasskey);
    if (outcome.kind === 'done') {
      await queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
      setState('idle');
      return;
    }
    setState(outcome.kind === 'cancelled' ? 'dismissed' : 'refused');
  };

  // Once by itself for each refusal, and before paint, so its line never shows ahead of the sheet. A
  // refusal that CLEARS re-arms it (a confirmation here, or one anywhere on the page that opened the
  // window), so a window that lapses later and refuses again asks again: never a dead end where the
  // content just vanishes (review-43 F2). Never while a sheet is up: this gate's own retry puts a
  // data-less read back to pending, which is not a refusal clearing.
  useLayoutEffect(() => {
    if (state === 'asking') return;
    if (first === undefined) {
      asked.current = false;
      return;
    }
    if (asked.current) return;
    asked.current = true;
    // CBG-FIX127: no sheet during the pause. The refusal counts as asked, so once the pause ends the
    // card offers its button rather than bringing the sheet up by itself.
    if (pauseEnd !== null) return;
    void ask();
    // `ask` closes over this render's reads; the guard keeps it to one ask per refusal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, state, pauseEnd]);

  if (first === undefined) return <>{gate.dialog}</>;

  if (pauseEnd !== null && state !== 'asking') {
    // S2: when it opens, and nothing to press.
    return (
      <>
        <Card testId={testId}>
          <CardBody className="flex flex-col items-start gap-12">
            <p
              className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${testId}-line`)}
            >
              {signingPauseWords.reads(pauseEnd)}
            </p>
          </CardBody>
        </Card>
        {gate.dialog}
      </>
    );
  }

  return (
    <>
      {/* A refusal standing with no sheet up always shows its sentence and the way back. */}
      {state !== 'asking' ? (
        <Card testId={testId}>
          <CardBody className="flex flex-col items-start gap-12">
            <p
              className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
              {...testIdProp(`${testId}-line`)}
            >
              {refusalSentence(
                first.error,
                committeeOnly ? COMMITTEE_EXECUTIVE_ASKS : EXECUTIVE_ASKS,
              )}
              {state === 'refused' ? ` ${DID_NOT_OPEN}` : ''}
            </p>
            <Button
              appearance="saas"
              tone="primary"
              onClick={() => void ask()}
              testId={`${testId}-confirm`}
            >
              Confirm it’s you
            </Button>
          </CardBody>
        </Card>
      ) : null}
      {gate.dialog}
    </>
  );
}
